import type { UserCredentials } from '@smartsash/api-client';
import { Banner, Icon, Typography } from '@smartsash/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './CredentialsPanel.module.css';

/** How long the "Copied" confirmation stays up before the button returns to its idle label. */
const COPIED_FEEDBACK_MS = 1500;

interface CopyRowProps {
  label: string;
  value: string;
  /** Render the value in a monospace face — a generated password is read character by character. */
  monospace?: boolean;
}

/**
 * One labelled, read-only credential with copy-to-clipboard.
 *
 * Deliberately not an `Input`: this is output, not a field. A disabled input would be
 * announced as a form control the user cannot fill, and a readonly one invites editing.
 */
function CopyRow({ label, value, monospace = false }: CopyRowProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const copy = () => {
    // The DOM types declare `clipboard` as always present; it is absent outside a secure
    // context, so the guard stays and the type is corrected instead (same as `StreamViewer/CopyField`).
    const clipboard = navigator.clipboard as Clipboard | undefined;
    void clipboard?.writeText(value);
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setCopied(false);
    }, COPIED_FEEDBACK_MS);
  };

  return (
    <div className={styles.row}>
      <Typography variant="text-sm" weight="medium" as="p" className={styles.label}>
        {label}
      </Typography>
      <div className={styles.value}>
        <Typography
          variant="text-md"
          weight="regular"
          as="p"
          className={monospace ? styles.mono : undefined}
        >
          {value}
        </Typography>
        <button
          type="button"
          className={styles['copy-btn']}
          aria-label={t('users.credentials.copyAria', { label })}
          onClick={copy}
        >
          <Icon name={copied ? 'check' : 'copy-01'} />
          <span>{copied ? t('users.credentials.copied') : t('users.credentials.copy')}</span>
        </button>
      </div>
    </div>
  );
}

export interface CredentialsPanelProps {
  credentials: UserCredentials;
}

/**
 * The one-time sign-in credentials for a freshly created (or password-regenerated) user.
 *
 * HS-1224: the backend returns `{ username, temporaryPassword }` exactly once — the password is
 * stored as a `SecureHasher` digest and can never be read back, so this render is the only
 * chance to hand it over. It is therefore held in a prop and nowhere else: never written to a
 * store, to `localStorage`/`sessionStorage`, to a query cache or to a log line, and it dies with
 * the modal that hosts it. The only way to get another one is
 * `POST /api/User/{institutionId}/{userId}/regenerate-temp-password`.
 */
function CredentialsPanel({ credentials }: CredentialsPanelProps) {
  const { t } = useTranslation();
  const { username, temporaryPassword, requiresPasswordChange } = credentials;

  return (
    <div className={styles.panel}>
      <Banner type="warning">{t('users.credentials.warning')}</Banner>
      {username && <CopyRow label={t('users.credentials.username')} value={username} />}
      {temporaryPassword && (
        <CopyRow
          label={t('users.credentials.temporaryPassword')}
          value={temporaryPassword}
          monospace
        />
      )}
      {requiresPasswordChange && (
        <Typography variant="text-sm" weight="regular" as="p" className={styles.note}>
          {t('users.credentials.mustChange')}
        </Typography>
      )}
    </div>
  );
}

export default CredentialsPanel;
